// RC10h · Asset Owners management page.
//
// RC10h correction: "Asset Owner" terminology throughout + popup styled
// with the SAME shared design tokens used by the Edit Asset dialog.
// All backgrounds use tailwind token classes (bg-white / bg-road-white),
// text uses CSS variables (var(--fg-primary)) and token classes
// (text-steel, text-coach-blue). No hard-coded hex values.
//
// Admins can create asset-owner organizations (coach_owner / trailer_owner),
// edit company fields, link / unlink canonical Contacts as owner-persons,
// archive and reactivate, and see which Fleet assets reference them.
//
// Reuses the EXISTING canonical Organization + Contact + ContactOrgRole
// models. The asset side stores only `owner_organization_id`.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { assetOwnerApi, contactApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Search, Archive, Undo2, Trash2 } from "lucide-react";

const BTN =
  "inline-flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium transition";
const PRIMARY = `${BTN} bg-coach-blue text-white hover:bg-deep-road-navy`;
const GHOST = `${BTN} border border-coach-silver text-graphite hover:bg-chrome-mist`;
const DESTRUCTIVE = `${BTN} border border-critical-red text-critical-red hover:bg-critical-red hover:text-white`;

const ORG_TYPE_LABEL = {
  coach_owner: "Coach owner",
  trailer_owner: "Trailer owner",
};

function AssetOwnersPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const d = await assetOwnerApi.list({
        q: q || undefined,
        include_archived: includeArchived,
        page_size: 100,
      });
      setRows(d.results || []);
      setTotal(d.total || 0);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const h = setTimeout(reload, 180);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, includeArchived]);

  return (
    <div className="p-6" data-testid="asset-owners-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-graphite">
            Asset Owners
          </h1>
          <p className="text-sm text-steel mt-1">
            Companies and individual owners whose assets we lease and operate.
            Edit here; Fleet assets reference these records by stable ID.
          </p>
        </div>
        <button
          data-testid="ao-create-btn"
          className={PRIMARY}
          onClick={() => setShowCreate(true)}
        >
          <Plus size={16} /> Add Asset Owner
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 bg-white border border-coach-silver rounded-sm px-3 py-1.5 flex-1 max-w-md">
          <Search size={14} className="text-steel" />
          <input
            data-testid="ao-search"
            className="flex-1 outline-none text-sm bg-transparent"
            style={{ color: "var(--fg-primary)" }}
            placeholder="Search company name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-graphite">
          <input
            data-testid="ao-include-archived"
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Include archived
        </label>
      </div>

      <div className="bg-white border border-coach-silver rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-road-white text-left">
            <tr>
              <th className="px-4 py-2 font-medium text-graphite">Company</th>
              <th className="px-4 py-2 font-medium text-graphite">Type</th>
              <th className="px-4 py-2 font-medium text-graphite">
                Billing City/State
              </th>
              <th className="px-4 py-2 font-medium text-graphite">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="ao-rows">
            {loading && (
              <tr>
                <td className="px-4 py-6 text-center text-steel" colSpan={5}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-steel" colSpan={5}>
                  No asset owners
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                data-testid={`ao-row-${r.id}`}
                className="border-t border-chrome-mist"
              >
                <td className="px-4 py-2">
                  <button
                    data-testid={`ao-open-${r.id}`}
                    className="text-coach-blue hover:underline font-medium text-left"
                    onClick={() => setEditing(r.id)}
                  >
                    {r.display_name}
                  </button>
                </td>
                <td className="px-4 py-2 text-steel">
                  {ORG_TYPE_LABEL[r.org_type] || r.org_type}
                </td>
                <td className="px-4 py-2 text-steel">
                  {r.billing_address?.city && r.billing_address?.state
                    ? `${r.billing_address.city}, ${r.billing_address.state}`
                    : "—"}
                </td>
                <td className="px-4 py-2">
                  {r.is_archived ? (
                    <span className="inline-flex items-center text-xs rounded-full border border-maintenance-orange text-maintenance-orange px-2 py-0.5">
                      Archived
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-xs rounded-full border border-coach-blue text-coach-blue px-2 py-0.5">
                      Active
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    data-testid={`ao-edit-${r.id}`}
                    className="text-sm text-coach-blue hover:underline"
                    onClick={() => setEditing(r.id)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-steel mt-2">
        {total} record{total === 1 ? "" : "s"}
      </div>

      {showCreate && (
        <CreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            setEditing(id);
            reload();
          }}
        />
      )}
      {editing && (
        <EditDialog
          id={editing}
          onClose={() => setEditing(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- CREATE
function CreateDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    display_name: "",
    org_type: "coach_owner",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.display_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      const d = await assetOwnerApi.create(form);
      toast.success("Asset owner created");
      onCreated(d.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-md bg-white p-0 gap-0 overflow-hidden w-full !block"
        data-testid="ao-create-dialog"
      >
        {/* 3-region shell — explicit flex-column container; shadcn's default
            grid/gap on DialogContent is overridden with p-0 gap-0 !block
            above and the explicit flex wrapper below. */}
        <div className="flex flex-col max-h-[90vh] md:max-h-[90vh] max-h-[100dvh]">
          <DialogHeader className="shrink-0 border-b border-coach-silver px-6 py-3 bg-white">
            <DialogTitle className="text-graphite">
              Add Asset Owner
            </DialogTitle>
          </DialogHeader>

          <form
            id="asset-owner-form"
            onSubmit={(e) => { e.preventDefault(); save(); }}
            className="min-h-0 flex-1 overflow-y-auto px-6 py-4 pb-6"
          >
            <div className="space-y-3">
              <L label="Company name">
                <input
                  data-testid="ao-create-name"
                  className="mt-1 w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
                  style={{ color: "var(--fg-primary)" }}
                  value={form.display_name}
                  onChange={(e) =>
                    setForm({ ...form, display_name: e.target.value })
                  }
                />
              </L>
              <L label="Type">
                <select
                  data-testid="ao-create-type"
                  className="mt-1 w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
                  style={{ color: "var(--fg-primary)" }}
                  value={form.org_type}
                  onChange={(e) =>
                    setForm({ ...form, org_type: e.target.value })
                  }
                >
                  <option value="coach_owner">Coach owner</option>
                  <option value="trailer_owner">Trailer owner</option>
                </select>
              </L>
            </div>
          </form>

          {/* Footer OUTSIDE the <form>; Save wires to it via form attribute.
              Plain <div> (not shadcn DialogFooter) so our justify-between +
              flex-wrap are not overridden by its sm:justify-end default. */}
          <div
            className="shrink-0 w-full border-t border-coach-silver bg-white px-6 py-3 flex items-center justify-between flex-wrap gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            data-testid="ao-create-footer"
          >
            <div />
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="ao-close"
                className={GHOST}
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="submit"
                form="asset-owner-form"
                data-testid="ao-submit"
                className={PRIMARY}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Asset Owner"}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- EDIT
function EditDialog({ id, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [savingCo, setSavingCo] = useState(false);
  const [contactQuery, setContactQuery] = useState("");
  const [contactOpts, setContactOpts] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);

  const load = async () => {
    try {
      const d = await assetOwnerApi.get(id);
      setDetail(d);
      setForm({
        display_name: d.display_name || "",
        legal_name: d.legal_name || "",
        primary_phone: d.primary_phone || "",
        email: d.email || "",
        notes: d.notes || "",
        billing_address: d.billing_address || {
          line1: "",
          line2: "",
          city: "",
          state: "",
          postal_code: "",
          country: "US",
        },
      });
    } catch (e) {
      toast.error(apiError(e));
      onClose();
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const q = contactQuery.trim();
    if (!q) {
      setContactOpts([]);
      return;
    }
    const h = setTimeout(async () => {
      setContactLoading(true);
      try {
        const d = await contactApi.search({ q, page_size: 10 });
        setContactOpts(d?.results || []);
      } catch {
        setContactOpts([]);
      } finally {
        setContactLoading(false);
      }
    }, 180);
    return () => clearTimeout(h);
  }, [contactQuery]);

  const saveCompany = async () => {
    setSavingCo(true);
    try {
      await assetOwnerApi.patch(id, form);
      toast.success("Saved");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingCo(false);
    }
  };

  const linkContact = async (contact_id) => {
    try {
      await assetOwnerApi.linkContact(id, contact_id);
      toast.success("Contact linked");
      setContactQuery("");
      setContactOpts([]);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const unlinkContact = async (rid) => {
    if (!window.confirm("Unlink this contact from the asset owner?")) return;
    try {
      await assetOwnerApi.unlinkContact(id, rid);
      toast.success("Contact unlinked");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this asset owner?")) return;
    try {
      await assetOwnerApi.archive(id);
      toast.success("Archived");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const reactivate = async () => {
    try {
      await assetOwnerApi.reactivate(id);
      toast.success("Reactivated");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!detail || !form) return null;
  const setAddr = (k, v) =>
    setForm({ ...form, billing_address: { ...form.billing_address, [k]: v } });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl bg-white p-0 gap-0 overflow-hidden w-full !block"
        data-testid="ao-edit-dialog"
      >
        {/* 3-region shell — flex-column container isolated from shadcn
            DialogContent grid/gap defaults via !block + p-0 gap-0 above. */}
        <div className="flex flex-col max-h-[90vh] md:max-h-[90vh] max-h-[100dvh]">
          {/* Region 1 — non-scrolling header */}
          <DialogHeader className="shrink-0 bg-white border-b border-coach-silver px-6 py-3">
            <DialogTitle className="flex items-center gap-2 text-graphite">
              Edit Asset Owner — {detail.display_name}
              {detail.is_archived && (
                <span className="text-xs rounded-full border border-maintenance-orange text-maintenance-orange px-2 py-0.5">
                  Archived
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Region 2 — independently scrollable form body. The <form>
              element IS the scroll container; the footer sits OUTSIDE it
              and submits via form="asset-owner-form". */}
          <form
            id="asset-owner-form"
            onSubmit={(e) => { e.preventDefault(); saveCompany(); }}
            className="min-h-0 flex-1 overflow-y-auto px-6 py-4 pb-6 space-y-6"
          >
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-graphite">Company</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <L label="Company / legal name" testid="ao-edit-display">
                  <Input value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} />
                </L>
                <L label="Legal name (optional)">
                  <Input value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} />
                </L>
                <L label="Primary phone">
                  <Input value={form.primary_phone} onChange={(v) => setForm({ ...form, primary_phone: v })} />
                </L>
                <L label="Email">
                  <Input value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                </L>
              </div>
              <h3 className="text-sm font-semibold text-graphite pt-2">Billing address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <L label="Line 1" testid="ao-edit-addr-line1"><Input value={form.billing_address?.line1 || ""} onChange={(v) => setAddr("line1", v)} /></L>
                <L label="Line 2"><Input value={form.billing_address?.line2 || ""} onChange={(v) => setAddr("line2", v)} /></L>
                <L label="City"><Input value={form.billing_address?.city || ""} onChange={(v) => setAddr("city", v)} /></L>
                <L label="State"><Input value={form.billing_address?.state || ""} onChange={(v) => setAddr("state", v)} /></L>
                <L label="ZIP"><Input value={form.billing_address?.postal_code || ""} onChange={(v) => setAddr("postal_code", v)} /></L>
                <L label="Country"><Input value={form.billing_address?.country || ""} onChange={(v) => setAddr("country", v)} /></L>
              </div>
              <L label="Internal notes">
                <textarea
                  data-testid="ao-edit-notes"
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 min-h-[80px] bg-white"
                  style={{ color: "var(--fg-primary)" }}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </L>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-graphite">Individual owners (contacts)</h3>
              <div className="bg-road-white border border-chrome-mist rounded-sm p-3">
                {detail.owner_people?.length === 0 && (
                  <div className="text-sm text-steel">No owner-person contacts linked.</div>
                )}
                <ul className="space-y-2" data-testid="ao-owner-people">
                  {(detail.owner_people || []).map((p) => (
                    <li
                      key={p.relationship_id}
                      data-testid={`ao-owner-person-${p.relationship_id}`}
                      className="flex items-center justify-between bg-white border border-coach-silver rounded-sm p-2"
                    >
                      <div className="text-sm" style={{ color: "var(--fg-primary)" }}>
                        <Link className="font-medium text-coach-blue hover:underline" to={`/contacts/${p.contact_id}`}>
                          {p.full_name}
                        </Link>
                        <span className="text-xs text-steel ml-2">
                          Owner · {p.primary_phone || "no phone"}
                        </span>
                      </div>
                      <button
                        type="button"
                        data-testid={`ao-owner-unlink-${p.relationship_id}`}
                        className={`${DESTRUCTIVE} text-xs`}
                        onClick={() => unlinkContact(p.relationship_id)}
                      >
                        <Trash2 size={12} /> Unlink
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <L label="Link an existing contact">
                <input
                  data-testid="ao-link-contact-search"
                  className="mt-1 w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
                  style={{ color: "var(--fg-primary)" }}
                  placeholder="Search contacts by name, phone, or email"
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                />
              </L>
              {contactLoading && <div className="text-xs text-steel">Searching…</div>}
              {contactOpts.length > 0 && (
                <ul className="border border-coach-silver rounded-sm max-h-40 overflow-y-auto bg-white">
                  {contactOpts.map((c) => (
                    <li
                      key={c.id}
                      data-testid={`ao-contact-opt-${c.id}`}
                      className="px-3 py-2 text-sm hover:bg-chrome-mist cursor-pointer border-b border-chrome-mist"
                      onClick={() => linkContact(c.id)}
                    >
                      <div className="font-medium" style={{ color: "var(--fg-primary)" }}>
                        {c.first_name} {c.last_name}
                      </div>
                      <div className="text-xs text-steel">
                        {c.mobile || c.office_phone || c.primary_email || "—"}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="text-sm font-semibold text-graphite">
                Fleet assets linked to this owner
              </h3>
              {detail.linked_assets?.length === 0 ? (
                <div className="text-sm text-steel mt-2">No assets reference this owner.</div>
              ) : (
                <ul className="mt-2 space-y-1" data-testid="ao-linked-assets">
                  {detail.linked_assets.map((a) => (
                    <li key={a.id} className="text-sm">
                      <Link className="text-coach-blue hover:underline" to={`/fleet/${a.id}`}>
                        {a.unit_name || a.fleet_number || a.id}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </form>

          {/* Region 3 — non-scrolling footer, Archive LEFT, Close+Save RIGHT.
              Plain <div> (not shadcn DialogFooter) so our justify-between +
              flex-wrap classes aren't overridden by DialogFooter's defaults. */}
          <div
            className="shrink-0 w-full border-t border-coach-silver bg-white px-6 py-3 flex items-center justify-between flex-wrap gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            data-testid="ao-edit-footer"
          >
            <div className="flex items-center">
              {detail.is_archived ? (
                <button
                  type="button"
                  data-testid="ao-edit-reactivate"
                  className={GHOST}
                  onClick={reactivate}
                >
                  <Undo2 size={16} /> Reactivate
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="ao-archive"
                  className={DESTRUCTIVE}
                  onClick={archive}
                >
                  <Archive size={16} /> Archive
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="ao-close"
                className={GHOST}
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="submit"
                form="asset-owner-form"
                data-testid="ao-submit"
                className={PRIMARY}
                disabled={savingCo}
              >
                {savingCo ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function L({ label, testid, children }) {
  return (
    <label className="block text-sm">
      <span className="text-graphite font-medium">{label}</span>
      <div data-testid={testid}>{children}</div>
    </label>
  );
}

function Input({ value, onChange }) {
  return (
    <input
      className="mt-1 w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
      style={{ color: "var(--fg-primary)" }}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default AssetOwnersPage;
